import { IArticle, IDocumentation, Mode } from '@gs/shared';
import { IPhotoEntityReference, ResponsiveAttribute } from '../../../basic';
import { ICategory } from '../../../categories/category';
import { GsStructBorderRadius, GsStructComponent, GsStructPageItems, GsStructSwiper } from '../component';
import { IMethod } from './../basic/method';
import { ITeaserBadgeSettings } from './teaser-badge-settings';

export interface IReportHeader {
  code: {
    ID?: number;
  };
}

export interface IReportRender {
  report: IReport;
  methods: IReportMethods;
}

export interface IReport {
  reportID: number;
  name: string;
  from: Date;
  to: Date;
  createdOn: Date;
  categories: ICategory[];
  tags: any[];
  attachments: any[];
  photos: IPhotoEntityReference[];
  medias: any[];
  paragraphs: IReportParagraph[];
  timePeriods: any[];
  ingredients: any[];
  description: string;
  appetizer: string;
  mainDish: string;
  dessert: string;
  garnish: string;
  countPersons: number;
  preparationMinutes: number;
  totalMinutes: number;
  priority: number;
  mainPhoto: IPhotoEntityReference;
  inactive: boolean;
  wantedCreatedOn?: Date;
  expectedReadingMinutes?: number;
  member: {
    displayName: string;
    iD: number;
  };
}

export interface GsStructReportDetailComponent
  extends GsStructComponent<IReportRender>, GsStructSwiper, GsStructPageItems, Omit<ITeaserBadgeSettings, 'showItemTypeBadge'> {
  /** How the items should be shown (list, slider, etc) */
  mode: Mode;
  /** The specified type-key for this struct */
  componentType: 'ReportDetail';
  /** Toggle for showing author or not */
  showAuthor: boolean;
  /** Toggle for showing date or not */
  showDate: boolean;
  /** Toggle for showing the share bar or not */
  showShareBar?: boolean;
  /** The ID of the displayed report */
  iD: string | number;
  /** Number of days to consider an article as new */
  newForDays: number;
  /** Height of the item images */
  itemImageHeight: ResponsiveAttribute<number>;
  /** Toggle for showing the photos in the report */
  showPhotos: boolean;
  /** The border radius for the paragraph photos in the report */
  borderRadius?: GsStructBorderRadius;
}

/**
 * Display settings of the report paragraphs (`report-body`). Satisfied by both
 * `GsStructReportDetailComponent` and `GsStructPlantDoctorComponent`, so either struct can be
 * passed straight through.
 */
export interface IReportBodySettings {
  /** How the items should be shown (list, slider, etc) */
  mode?: Mode;
  /** Toggle for showing the photos in the report */
  showPhotos?: boolean;
  /** Number of days to consider an article as new */
  newForDays?: number;
  /** Whether to show the item badge (master toggle for the other badges) */
  showItemBadge?: boolean;
  /** Whether to show the discount badge */
  showDiscountBadge?: boolean;
  /** Whether to show the available status badge */
  showAvailableStatusBadge?: boolean;
  /** Whether to show the article teaser badge */
  showArticleTeaserBadge?: boolean;
  /** Height of the item images */
  itemImageHeight?: ResponsiveAttribute<number>;
  /** The border radius for the paragraph photos in the report */
  borderRadius?: GsStructBorderRadius;
}

export interface IReportParagraph {
  paragraphID: number;
  documentations: IDocumentation[];
  title: string;
  text: string;
  picture: IPhotoEntityReference;
  position: number;
  photos: IPhotoEntityReference[];
  articles: IArticle[];
  medias: any[];
  report: IReportRender;
  type: ReportParagraphType;
  /** FAQs belonging to this paragraph (only set for the Accordion type) */
  fAQs?: IReportParagraphFaq[];
}

export interface IReportParagraphFaq {
  /** Unique identifier of the FAQ */
  faqID: number;
  /** The question shown as the accordion header */
  question: string;
  /** The answer shown as the accordion content (markup) */
  answer: string;
  /** Sort order of the FAQ within the paragraph */
  order?: number;
  /** Photos belonging to the FAQ */
  photos?: IPhotoEntityReference[];
}

export interface IReportMethods {
  /** Method to play a video inside the report (loads the urls for the src) */
  play: IMethod;
  /** Method to download a document */
  downloadDocument: IMethod;
}

export enum ReportParagraphType {
  /** Fließtext // Runningtext */
  RunningText = 0,
  /** Anlauftext // Warmuptext */
  WarmupText = 1,
  /** Speziell // Special */
  Special = 2,
  /** Aufzählung // Bullet */
  Bullet = 3,
  /** Zitat // Quote */
  Quote = 4,
  /** Accordion (FAQ) */
  Accordion = 5
}
